import _ from 'lodash'
import { memo, useCallback, useEffect, useRef } from 'react'
import shallow from 'zustand/shallow'
import { useDrag } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'
import { useStore } from '../../store/store.js'
import Box from './Box.jsx'
import { ItemTypes, MASKS_TYPE, viewerTypes, getPrimaryDark } from '../../constants/constants.js'
import { getBoxById, getMaskById, getContainerByColumnId } from '../../store/lookups'
import { effectiveZoom } from '../../utils/zoom'
import { groupFrameZIndex } from '../../utils/boxes'
import { beginSnapSession, endSnapSession, flushSnap, resolveSnapOffset } from '../../snapping/snapSession'

const getStyles = (left, top, rotation, isDragging, drawMode, fromViewer, isSelected, locked, accentColor) => {
  if (fromViewer === viewerTypes.VARIABLES) {
    return isSelected ? { outline: '2px solid ' + accentColor } : {}
  }
  if (!drawMode)
    return {}
  const positionTransform = `translate3d(${left}px, ${top}px, 0)`;
  const rotationTransform = _.isNumber(rotation) ? ` rotate(${rotation}deg)` : '';
  const transform = positionTransform + rotationTransform;
  return {
    position: 'absolute',
    transform,
    WebkitTransform: transform,
    // IE fallback: hide the real node using CSS when dragging
    // because IE will ignore our custom "empty image" drag preview.
    opacity: isDragging ? 0 : 1,
    transition: 'opacity 0.1s ease-in-out',
    cursor: !fromViewer ? (locked ? 'default' : 'grab') : 'inherit',
  }
}

const selector =
  (id) =>
    ({
      slider,
      boxes,
      masks,
      containers,
      currentProposal,
      currentDocument,
    }) => {
      const box = getBoxById(boxes, masks, id);
      const accentColor = getPrimaryDark(currentDocument?.type === 'template');

      if (!box) {
        return {
          isSlided: false,
          box: undefined,
          container: undefined,
          currentProposal,
          maskTypeEdiatble: true,
          boxesInGroup: undefined,
          accentColor
        };
      }

      const container = getContainerByColumnId(containers, box.columnId)
      const maskType = getMaskById(masks, MASKS_TYPE.HEADER.id)?.editableMask;
      const maskTypeEdiatble = typeof maskType !== 'boolean' ? true : maskType;

      let boxesInGroup;
      if (box.type === ItemTypes.GROUP_BOXES && box.boxIds) {
        boxesInGroup = [];
        for (const b of boxes) {
          if (box.boxIds.includes(b.id)) boxesInGroup.push(b);
        }
        for (const m of masks) {
          for (const b of m.boxes) {
            if (box.boxIds.includes(b.id)) boxesInGroup.push(b);
          }
        }
      }

      return {
        isSlided: slider.boxes.includes(id),
        box,
        container,
        currentProposal,
        maskTypeEdiatble,
        boxesInGroup,
        accentColor
      }
    };

export const Dragger = memo(function Dragger({ id, drawMode, fromViewer, isSelected, columnWidth }) {
  const {
    box,
    container,
    currentProposal,
    maskTypeEdiatble,
    boxesInGroup,
    accentColor
  } = useStore(selector(id), shallow);
  const zoomLevel = useStore((state) => state.zoomLevel)
  const landscape = useStore((state) => state.landscape)
  const moveBoxInfinite = useStore(({ moveBoxInfinite }) => moveBoxInfinite)
  const tableDragRef = useRef(null)
  const draggerDivRef = useRef(null)
  // Hors mode dessin (positionnement libre) et hors saut de ligne, exclu du
  // maxWidth côté Resizer.
  const clampedWidth =
    !drawMode &&
    box?.type !== ItemTypes.LINE_BREAK &&
    _.isNumber(box?.width) &&
    _.isNumber(columnWidth)
      ? Math.min(box.width, columnWidth)
      : undefined
  const parentId = box.columnId || box.maskId || box.pageId
  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: box.type,
      // `item` en fonction : react-dnd l'appelle au démarrage du drag, ce qui
      // en fait le point d'accroche naturel pour ouvrir la session de
      // magnétisme (photographie des cibles d'alignement) sans ajouter de
      // listener ni toucher au flux de drag existant.
      item: () => {
        if (drawMode && !fromViewer) {
          beginSnapSession({
            parentId,
            draggedId: id,
            draggedEl: draggerDivRef.current,
            // Un groupe se déplace d'un bloc : ses membres ne sont pas des
            // cibles d'alignement pour lui-même.
            excludeIds: [id, ...(box.boxIds ?? [])],
          })
        }
        return {
          id,
          left: box.left,
          top: box.top,
          rotation: box.rotation,
          parentId,
          boxIds: box.boxIds
        }
      },
      end: () => endSnapSession(),
      canDrag: () => !box.lockedPosition,
      collect: (monitor) => ({
        isDragging: monitor.isDragging() || (monitor.getItem()?.boxIds?.includes(id) && !monitor.getItem()?.copied),
      })
    }),
    [id, box.left, box.top, box.lockedPosition, box.boxIds, box.rotation, parentId, drawMode, fromViewer],
  )

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dragRule = useCallback(() => {
    if (drawMode && !fromViewer) {
      if (box.lockedPosition) return undefined;
      // Table in drawMode uses custom mouse drag via overlay — react-dnd not used
      if (box.type === ItemTypes.TABLE) return undefined;
      if (currentProposal.id === null && container?.from_linked_section) return undefined;
      if (currentProposal.id === null) return drag
      if (currentProposal.id !== null) {
        if ((box.maskId === "header" || box.maskId === "footer") && !maskTypeEdiatble && box.type !== "img") return undefined
        if ((typeof container?.editable !== 'boolean' || container?.editable)) return drag
        if (typeof container?.editable === 'boolean' && !container?.editable && box.type === "img") return drag
      }
    } else {
      return undefined
    }
  }, [container, drawMode, drag, box, maskTypeEdiatble, fromViewer])

  const handleTableMouseDown = useCallback((e) => {
    if (!drawMode || box.type !== ItemTypes.TABLE || fromViewer || box.lockedPosition) return

    e.preventDefault() // Prevent DraftJS focus and text selection during drag

    const startX = e.clientX
    const startY = e.clientY
    const startLeft = box.left
    const startTop = box.top
    const zoomFactor = effectiveZoom(zoomLevel, landscape) / 100
    const rotationTransform = _.isNumber(box.rotation) ? ` rotate(${box.rotation}deg)` : ''

    tableDragRef.current = false

    // Le tableau se déplace à la souris (react-dnd inutilisable ici) : même
    // moteur de magnétisme, branché sur ce flux au lieu du drag HTML5.
    const snappedOffsetFrom = (moveEvent) =>
      resolveSnapOffset({
        x: (moveEvent.clientX - startX) / zoomFactor,
        y: (moveEvent.clientY - startY) / zoomFactor,
      })

    const handleMouseMove = (moveEvent) => {
      if (tableDragRef.current === null) return
      const rawDx = moveEvent.clientX - startX
      const rawDy = moveEvent.clientY - startY
      if (!tableDragRef.current && (Math.abs(rawDx) > 3 || Math.abs(rawDy) > 3)) {
        tableDragRef.current = true
        // Ouverte au franchissement du seuil de drag, pas au mousedown : un
        // simple clic ne doit afficher aucun guide.
        beginSnapSession({ parentId, draggedId: box.id, draggedEl: draggerDivRef.current })
      }
      if (tableDragRef.current && draggerDivRef.current) {
        const offset = snappedOffsetFrom(moveEvent)
        const newLeft = startLeft + offset.x
        const newTop = startTop + offset.y
        draggerDivRef.current.style.transform = `translate3d(${newLeft}px, ${newTop}px, 0)${rotationTransform}`
        flushSnap()
      }
    }

    const handleMouseUp = (upEvent) => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      if (!tableDragRef.current) {
        tableDragRef.current = null
        endSnapSession()
        return
      }
      const offset = snappedOffsetFrom(upEvent)
      const newLeft = Math.round(startLeft + offset.x)
      const newTop = Math.round(startTop + offset.y)
      moveBoxInfinite(newLeft, newTop, box.id)
      tableDragRef.current = null
      endSnapSession()
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
  }, [box, drawMode, fromViewer, zoomLevel, landscape, moveBoxInfinite, parentId])

  return (
    <div
      ref={(el) => {
        draggerDivRef.current = el
        const dndRef = dragRule()
        if (typeof dndRef === 'function') dndRef(el)
      }}
      id={`box-container-${box?.id ? box.id : 'new'}`}
      onMouseDown={handleTableMouseDown}
      style={{
        ...getStyles(
          box.left,
          box.top,
          box.rotation,
          isDragging,
          drawMode,
          fromViewer,
          isSelected,
          box.lockedPosition,
          accentColor),
        zIndex: boxesInGroup ? groupFrameZIndex(boxesInGroup) : box?.zIndex,
      }}
    >
      {/* Même borne que dans Resizer : la largeur stockée peut dater d'avant un
          rétrécissement de la colonne (ou de la cellule, pour une colonne
          découpée en lignes) et déborderait alors au survol. */}
      <Box
        id={id}
        width={clampedWidth}
        drawMode={drawMode}
        fromViewer={fromViewer}
        isSelected={isSelected}
      />
    </div>
  )
})

// Variante allégée pour la génération PDF : pas de useDrag ni de listeners de
// drag (jamais déclenchés par Puppeteer). Le DOM produit est identique au
// Dragger (mêmes styles via getStyles, isDragging/isSelected toujours false en
// PDF), seule la machinerie react-dnd disparaît.
export const DraggerPdf = memo(function DraggerPdf({ id, drawMode, fromViewer, isSelected }) {
  const { box, boxesInGroup, accentColor } = useStore(selector(id), shallow);

  if (!box) return null;

  return (
    <div
      id={`box-container-${box?.id ? box.id : 'new'}`}
      style={{
        ...getStyles(
          box.left,
          box.top,
          box.rotation,
          false,
          drawMode,
          fromViewer,
          isSelected,
          box.lockedPosition,
          accentColor),
        zIndex: boxesInGroup ? groupFrameZIndex(boxesInGroup) : box?.zIndex,
      }}
    >
      <Box id={id} drawMode={drawMode} fromViewer={fromViewer} isSelected={isSelected} />
    </div>
  )
})
